import { emptyV2Api as api } from "../emptyV2Api";

const MARKETPLACE_ORDERS_PATH = "/redemption/admin/ws-order";

export type MarketplaceOrder = {
  OrderId: string;
  ShopId: number;
  [key: string]: unknown;
};

export type MarketplaceOrdersPage = {
  Items: MarketplaceOrder[];
  Count: number;
  LastEvaluatedKey?: {
    OrderId: string;
    ShopId: number;
  };
};

export type MarketplaceOrdersResponse = {
  Items: MarketplaceOrder[];
  Count: number;
};

type MarketplaceOrdersParams = {
  scope: "all";
  lek_oid?: string;
  lek_sid?: string;
};

type FetchMarketplaceOrdersPage = (
  params: MarketplaceOrdersParams,
) => Promise<unknown>;

function isMarketplaceOrder(value: unknown): value is MarketplaceOrder {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;

  const order = value as Partial<MarketplaceOrder>;
  return typeof order.OrderId === "string"
    && typeof order.ShopId === "number"
    && Number.isInteger(order.ShopId);
}

function parseMarketplaceOrdersPage(value: unknown): MarketplaceOrdersPage {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Marketplace orders response was not an object");
  }

  const page = value as Partial<MarketplaceOrdersPage>;
  if (!Array.isArray(page.Items) || !page.Items.every(isMarketplaceOrder)) {
    throw new Error("Marketplace orders response did not include valid Items");
  }

  const lastKey = page.LastEvaluatedKey;
  if (lastKey !== undefined && (
    !lastKey
    || typeof lastKey !== "object"
    || typeof lastKey.OrderId !== "string"
    || typeof lastKey.ShopId !== "number"
    || !Number.isInteger(lastKey.ShopId)
  )) {
    throw new Error("Marketplace orders response included an invalid pagination key");
  }

  return {
    Items: page.Items,
    Count: page.Items.length,
    LastEvaluatedKey: lastKey,
  };
}

export async function collectMarketplaceOrders(
  fetchPage: FetchMarketplaceOrdersPage,
): Promise<MarketplaceOrdersResponse> {
  const items: MarketplaceOrder[] = [];
  let cursor: MarketplaceOrdersPage["LastEvaluatedKey"];

  do {
    const params: MarketplaceOrdersParams = { scope: "all" };
    if (cursor) {
      params.lek_oid = cursor.OrderId;
      params.lek_sid = cursor.ShopId.toString();
    }

    const page = parseMarketplaceOrdersPage(await fetchPage(params));
    items.push(...page.Items);
    cursor = page.LastEvaluatedKey;
  } while (cursor);

  return {
    Items: items,
    Count: items.length,
  };
}

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getAllMarketplaceOrders: build.query<MarketplaceOrdersResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const data = await collectMarketplaceOrders(async (params) => {
            const result = await fetchWithBQ({
              url: MARKETPLACE_ORDERS_PATH,
              method: "GET",
              params,
            });

            if (result.error) {
              throw new Error("Failed to fetch marketplace orders");
            }

            return result.data;
          });
          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error
                ? error.message
                : "Failed to fetch marketplace orders",
            },
          };
        }
      },
    }),
  }),
});

export const { useLazyGetAllMarketplaceOrdersQuery } = injectedApi;
